/**
 * Migration Progress Logger
 *
 * Utility for tracking and logging migration progress from AWS Amplify to AWS SDK.
 * Helps identify which code paths are using legacy Amplify vs new SDK implementations.
 */

export enum MigrationPhase {
  CONFIG = 'config',
  AUTH = 'auth',
  GRAPHQL = 'graphql',
  API_ROUTES = 'api_routes',
  STORAGE = 'storage',
  REPOSITORIES = 'repositories',
}

export enum MigrationStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ROLLED_BACK = 'rolled_back',
}

interface MigrationLogEntry {
  timestamp: string;
  phase: MigrationPhase;
  status: MigrationStatus;
  message: string;
  context?: Record<string, any>;
  error?: Error;
}

class MigrationLogger {
  private logs: MigrationLogEntry[] = [];
  private readonly MAX_LOGS = 1000;
  private enabled: boolean;

  constructor() {
    this.enabled =
      process.env.NODE_ENV === 'development' ||
      process.env.NEXT_PUBLIC_ENABLE_MIGRATION_LOGGING === 'true';
  }

  /**
   * Log a migration event
   */
  log(
    phase: MigrationPhase,
    status: MigrationStatus,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ): void {
    if (!this.enabled) {
      return;
    }

    const entry: MigrationLogEntry = {
      timestamp: new Date().toISOString(),
      phase,
      status,
      message,
      context,
      error,
    };

    this.logs.push(entry);

    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }

    this.logToConsole(entry);
  }

  /**
   * Log to browser console with formatting
   */
  private logToConsole(entry: MigrationLogEntry): void {
    const prefix = '🔄 [Migration]';
    const phaseTag = `[${entry.phase.toUpperCase()}]`;
    const statusEmoji = this.getStatusEmoji(entry.status);

    const message = `${prefix} ${phaseTag} ${statusEmoji} ${entry.message}`;

    if (entry.error) {
      console.error(message, entry.context, entry.error);
    } else if (entry.status === MigrationStatus.COMPLETED) {
      console.log(`%c${message}`, 'color: #4caf50; font-weight: bold', entry.context);
    } else if (entry.status === MigrationStatus.IN_PROGRESS) {
      console.log(`%c${message}`, 'color: #2196f3', entry.context);
    } else if (entry.status === MigrationStatus.ROLLED_BACK) {
      console.warn(message, entry.context);
    } else {
      console.log(message, entry.context);
    }
  }

  /**
   * Get emoji for status
   */
  private getStatusEmoji(status: MigrationStatus): string {
    switch (status) {
      case MigrationStatus.NOT_STARTED:
        return '⏸️';
      case MigrationStatus.IN_PROGRESS:
        return '🔄';
      case MigrationStatus.COMPLETED:
        return '✅';
      case MigrationStatus.ROLLED_BACK:
        return '⏪';
      default:
        return '❓';
    }
  }

  /**
   * Log when using legacy Amplify code
   */
  logAmplifyUsage(component: string, method: string, context?: Record<string, any>): void {
    this.log(
      MigrationPhase.CONFIG,
      MigrationStatus.IN_PROGRESS,
      `Using Amplify legacy code: ${component}.${method}`,
      context
    );
  }

  /**
   * Log when using new AWS SDK code
   */
  logSdkUsage(component: string, method: string, context?: Record<string, any>): void {
    this.log(
      MigrationPhase.CONFIG,
      MigrationStatus.COMPLETED,
      `Using AWS SDK: ${component}.${method}`,
      context
    );
  }

  /**
   * Log an error during migration
   */
  logError(
    phase: MigrationPhase,
    message: string,
    error: Error,
    context?: Record<string, any>
  ): void {
    this.log(phase, MigrationStatus.IN_PROGRESS, message, context, error);
  }

  /**
   * Get all logs
   */
  getLogs(): MigrationLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs for a specific phase
   */
  getLogsByPhase(phase: MigrationPhase): MigrationLogEntry[] {
    return this.logs.filter((log) => log.phase === phase);
  }

  /**
   * Get logs by status
   */
  getLogsByStatus(status: MigrationStatus): MigrationLogEntry[] {
    return this.logs.filter((log) => log.status === status);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Generate migration report
   */
  generateReport(): string {
    const report: string[] = [];

    report.push('=== AWS SDK Migration Report ===');
    report.push(`Generated: ${new Date().toISOString()}`);
    report.push(`Total logs: ${this.logs.length}`);
    report.push('');

    Object.values(MigrationPhase).forEach((phase) => {
      const phaseLogs = this.getLogsByPhase(phase);
      if (phaseLogs.length > 0) {
        report.push(`\n## Phase: ${phase.toUpperCase()}`);
        report.push(`Total events: ${phaseLogs.length}`);

        Object.values(MigrationStatus).forEach((status) => {
          const count = phaseLogs.filter((log) => log.status === status).length;
          if (count > 0) {
            report.push(`  ${status}: ${count}`);
          }
        });
      }
    });

    const errors = this.logs.filter((log) => log.error);
    if (errors.length > 0) {
      report.push('\n## Recent Errors');
      errors.slice(-10).forEach((log) => {
        report.push(`- [${log.timestamp}] ${log.phase}: ${log.message}`);
        if (log.error) {
          report.push(`  Error: ${log.error.message}`);
        }
      });
    }

    return report.join('\n');
  }

  /**
   * Export logs as JSON
   */
  exportLogsAsJson(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Enable or disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
}

export const migrationLogger = new MigrationLogger();

if (typeof window !== 'undefined') {
  (window as any).migrationLogger = migrationLogger;
}

export function logAmplifyUsage(
  component: string,
  method: string,
  context?: Record<string, any>
): void {
  migrationLogger.logAmplifyUsage(component, method, context);
}

export function logSdkUsage(
  component: string,
  method: string,
  context?: Record<string, any>
): void {
  migrationLogger.logSdkUsage(component, method, context);
}

export function logMigrationError(
  phase: MigrationPhase,
  message: string,
  error: Error,
  context?: Record<string, any>
): void {
  migrationLogger.logError(phase, message, error, context);
}
